import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import confetti from "canvas-confetti";
import { Check } from "lucide-react";

interface CelebrationAnimationProps {
  /** Called when the celebration animation completes (~2s) */
  onComplete?: () => void;
}

/**
 * Celebration animation with confetti burst and animated checkmark.
 * Respects prefers-reduced-motion: shows static checkmark, no confetti.
 */
export function CelebrationAnimation({
  onComplete,
}: CelebrationAnimationProps) {
  const prefersReducedMotion = useReducedMotion();
  const [animationDone, setAnimationDone] = useState(false);
  const hasFireRef = useRef(false);

  useEffect(() => {
    if (prefersReducedMotion) {
      setAnimationDone(true);
      onComplete?.();
      return;
    }

    if (hasFireRef.current) return;
    hasFireRef.current = true;

    // Fire confetti burst with green brand colors
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.6 },
      colors: ["#22c55e", "#16a34a", "#4ade80", "#86efac"],
      disableForReducedMotion: true,
    });

    const timer = setTimeout(() => {
      setAnimationDone(true);
      onComplete?.();
    }, 2000);

    return () => clearTimeout(timer);
  }, [prefersReducedMotion, onComplete]);

  if (prefersReducedMotion) {
    return (
      <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
        <Check className="h-10 w-10 text-green-600 dark:text-green-400" />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Pulsing ring */}
      <motion.div
        className="absolute inset-0 rounded-full bg-green-400/20"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={
          animationDone
            ? { scale: 1.4, opacity: [0.3, 0], transition: { repeat: Infinity, duration: 2 } }
            : { scale: [0.8, 1.6], opacity: [0.5, 0] }
        }
        transition={{ duration: 1, ease: "easeOut" }}
      />

      {/* Checkmark circle */}
      <motion.div
        className="relative flex h-20 w-20 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{
          type: "spring",
          stiffness: 200,
          damping: 15,
          duration: 0.8,
        }}
      >
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 300, damping: 20 }}
        >
          <Check className="h-10 w-10 text-green-600 dark:text-green-400" />
        </motion.div>
      </motion.div>
    </div>
  );
}
